/**
 * The adversarial design review as a pure async helper, extracted from
 * ReviewDialog so the Co-Designer can run it conversationally: the pushbacks
 * land as chat turns instead of a modal list.
 */
import type { DesignProject } from '@volley/crucible-ipc-types';
import type { CrucibleClient } from '@volley/crucible-client';
import { aiChatStream, readSection } from '../anvil-api';
import { REVIEW_GUIDE, reviewLevelMeta } from '../prompts';
import { ReviewQuestionsSchema, parseAiJson } from '../schemas';
import { buildLensBlock, type LensRef } from './hammer-flow';

export interface ReviewQuestion {
  id: string;
  title: string;
  question: string;
  area?: string;
  severity?: 'low' | 'medium' | 'high';
  why?: string;
  /** Concrete edit instruction suitable for pre-filling freeform revision. */
  recommendation?: string;
}

const SEVERITY_ORDER = { high: 0, medium: 1, low: 2 } as const;

/** Run the adversarial review and return questions sorted by severity. */
export async function runDesignReview(
  client: CrucibleClient,
  opts: {
    gameId: string;
    project: DesignProject;
    lensIds?: LensRef[];
    /** Adversarial level 1–5 (persona); defaults to the balanced 3. */
    level?: number;
    questionCount?: number;
    focus?: string;
  },
): Promise<ReviewQuestion[]> {
  const { gameId, project, lensIds = [], level = 3, questionCount = 6, focus = '' } = opts;
  const lensBlock = await buildLensBlock(client, lensIds);

  // Full design dump.
  const dumpParts: string[] = [];
  for (const s of [...project.sections].sort((a, b) => a.order - b.order)) {
    let body = '';
    try {
      body = await readSection(client, gameId, s.filename);
    } catch {
      // skip unreadable
    }
    dumpParts.push(`\n## ${s.id} — ${s.title} [${s.doc.toUpperCase()}]\n${body}`);
  }

  const meta = reviewLevelMeta(level);
  const distribution = level >= 4 ? 'half' : level >= 3 ? 'a third' : 'a quarter';
  const system =
    REVIEW_GUIDE +
    lensBlock +
    `\n\n# Reviewer persona (adversarial level ${meta.level}/5: ${meta.label})\n${meta.persona}` +
    `\n\nTask: Read the ${project.projectType.toUpperCase()} for "${project.name}" and raise ${questionCount} pointed review questions a designer must answer before this design is ready for build.${
      focus.trim() ? `\n\nFocus the review specifically on: ${focus.trim()}` : ''
    }\n\nOutput strict JSON:\n{ "questions": [ { "id": "q1", "title": "5-8 word headline", "question": "full question, 1-3 sentences, ending in a question mark", "area": "section-id — short label", "severity": "low|medium|high", "why": "1-2 sentences on what concern this surfaces", "recommendation": "optional concrete imperative edit instruction for a design revision agent" } ] }\n\nRules:\n- Each must point at a specific section/decision/assumption actually in the design.\n- Vary the areas.\n- Severity reflects real build risk, not tone.\n- Only include recommendation when there is a specific, actionable design change the revision agent can propose. Leave it empty for generic advice or questions that require a human answer first.\n- Recommendations may target one section or multiple sections, but must be phrased as concrete edit instructions.\n- At level ${meta.level}/5, around ${distribution} of questions should be "high".`;

  const { done } = aiChatStream(client, {
    gameId,
    system,
    messages: [{ role: 'user', content: `Design under review:\n${dumpParts.join('\n')}` }],
    maxTokens: 8192,
  });
  const result = await done;
  const parsed = parseAiJson(result.text, ReviewQuestionsSchema);
  return [...(parsed.questions ?? [])].sort(
    (a, b) =>
      (SEVERITY_ORDER[a.severity ?? 'medium'] ?? 1) - (SEVERITY_ORDER[b.severity ?? 'medium'] ?? 1),
  );
}

/** Render one pushback as a chat-friendly markdown turn. */
export function reviewQuestionToMarkdown(q: ReviewQuestion): string {
  const parts = [`**${q.title}**${q.severity ? ` _(${q.severity})_` : ''}`, '', q.question];
  if (q.why) parts.push('', `_Why it matters:_ ${q.why}`);
  if (q.area) parts.push('', `_Area:_ ${q.area}`);
  if (q.recommendation?.trim()) parts.push('', `_Recommendation:_ ${q.recommendation.trim()}`);
  return parts.join('\n');
}
